/* eslint-disable no-self-assign */
/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { getSupportedInputTypes, Platform } from '@angular/cdk/platform';
import { AutofillMonitor } from '@angular/cdk/text-field';
import {
    Directive,
    DoCheck,
    ElementRef,
    HostBinding,
    HostListener,
    Inject,
    InjectionToken,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Self
} from '@angular/core';
import { FormGroupDirective, NgControl, NgForm } from '@angular/forms';

import { Subject } from 'rxjs';

import { ErrorStateMatcher } from '../error-options';
import { mixinErrorState, CanUpdateErrorState, CanUpdateErrorStateCtor } from '../error-state';
import { RidangoFormFieldControl } from '../form-field.control';
import { TranslateService } from '@ngx-translate/core';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
let nextUniqueId = 0;

export const RIDANGO_INPUT_VALUE_ACCESSOR = new InjectionToken<{ value: any }>('RIDANGO_INPUT_VALUE_ACCESSOR');

class RidangoInputBase {
    constructor(
        public _defaultErrorStateMatcher: ErrorStateMatcher,
        public _parentForm: NgForm,
        public _parentFormGroup: FormGroupDirective,
        /** @docs-private */
        public ngControl: NgControl
    ) {}
}
const _RidangoInputMixinBase: CanUpdateErrorStateCtor & typeof RidangoInputBase = mixinErrorState(RidangoInputBase);

@Directive({
    selector: '[ridangoInput]',
    exportAs: 'ridangoInput',
    providers: [{ provide: RidangoFormFieldControl, useExisting: InputDirective }]
})
/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
export class InputDirective
    extends _RidangoInputMixinBase
    implements RidangoFormFieldControl<any>, OnChanges, OnDestroy, OnInit, DoCheck, CanUpdateErrorState
{
    // TODO: CanUpdateErrorState implement
    protected _uid = `ridango-input-${nextUniqueId++}`;
    protected _previousNativeValue: string;
    protected _id: string;
    protected _disabled = false;
    protected _required = false;
    protected _type = 'text';
    private _readonly = false;
    protected _neverEmptyInputTypes = ['date', 'datetime', 'datetime-local', 'month', 'time', 'week'].filter((t) =>
        getSupportedInputTypes().has(t)
    );

    private _inputValueAccessor: { value: any };
    private _keyboardListener?: PluginListenerHandle;
    /** The aria-describedby attribute on the input for improved a11y. */
    private _ariaDescribedBy: string;
    private _hasCustomDescribedBy = false;

    /** Whether the component is being rendered on the server. */
    _isServer = false;

    /** Whether the component is a native html select. */
    _isNativeSelect = false;

    /** Whether the component is a native html textarea. */
    _isNativeTextarea = false;

    /**
     * Implemented as part of RidangoFormFieldControl.
     * @docs-private
     */
    focused = false;

    /**
     * Implemented as part of RidangoFormFieldControl.
     * @docs-private
     */
    readonly stateChanges: Subject<void> = new Subject<void>();

    /**
     * Implemented as part of RidangoFormFieldControl.
     * @docs-private
     */
    controlType = 'ridango-input';

    /**
     * Implemented as part of RidangoFormFieldControl.
     * @docs-private
     */
    autofilled = false;

    /** An object used to control when error messages are shown. */
    // TODO:
    @Input() errorStateMatcher: ErrorStateMatcher;
    @Input() label = '';

    /**
     * Implemented as part of RidangoFormFieldControl.
     * @docs-private
     */
    @Input() placeholder: string;

    constructor(
        protected _elementRef: ElementRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
        protected _platform: Platform,
        /** @docs-private */
        @Optional() @Self() public ngControl: NgControl,
        @Optional() _parentForm: NgForm,
        @Optional() _parentFormGroup: FormGroupDirective,
        _defaultErrorStateMatcher: ErrorStateMatcher,
        @Optional() @Self() @Inject(RIDANGO_INPUT_VALUE_ACCESSOR) inputValueAccessor: any,
        private _autofillMonitor: AutofillMonitor,
        private translate: TranslateService,
        ngZone: NgZone
    ) {
        super(_defaultErrorStateMatcher, _parentForm, _parentFormGroup, ngControl);

        const element = this._elementRef.nativeElement;

        // If no input value accessor was explicitly specified, use the element as the input value
        // accessor.
        // TODO:
        this._inputValueAccessor = inputValueAccessor || element;

        this._previousNativeValue = this.value;

        // Force setter to be called in case id was not specified.
        // eslint-disable-next-line no-self-assign
        this.id = this.id;

        // On some versions of iOS the caret gets stuck in the wrong place when holding down the delete
        // key. In order to get around this we need to "jiggle" the caret loose. Since this bug only
        // exists on iOS, we only bother to install the listener on iOS.
        if (_platform.IOS) {
            ngZone.runOutsideAngular(() => {
                _elementRef.nativeElement.addEventListener('keyup', (event: Event) => {
                    const el = event.target as HTMLInputElement;
                    if (!el.value && !el.selectionStart && !el.selectionEnd) {
                        // Note: Just setting `0, 0` doesn't fix the issue. Setting
                        // `1, 1` fixes it for the first time that you type text and
                        // then hold delete. Toggling to `1, 1` and then back to
                        // `0, 0` seems to completely fix it.
                        el.setSelectionRange(1, 1);
                        el.setSelectionRange(0, 0);
                    }
                });
            });
        }

        this._isServer = !this._platform.isBrowser;
        this._isNativeTextarea = element.nodeName.toLowerCase() === 'textarea';
        this._isNativeSelect = element.nodeName.toLowerCase() === 'select';

        if (this._isNativeSelect) {
            this.controlType = (element as HTMLSelectElement).multiple
                ? 'ridango-native-select-multiple'
                : 'ridango-native-select';
        }
    }

    // @breaking-change 8.0.0 remove .ridango-form-field-autofill-control in favor of AutofillMonitor.
    @HostBinding('class')
    readonly hostClass = 'ridango-input-element ridango-form-field-autofill-control';

    @HostBinding('class.ridango-input-server')
    get hostIsServer() {
        return this._isServer;
    }

    @HostBinding('class.ridango-input-textarea')
    get hostIsTextarea() {
        return this._isNativeTextarea;
    }

    // Native input properties that are overwritten by Angular inputs need to be synced with
    // the native input element. Otherwise property bindings for those don't work.
    @HostBinding('attr.id')
    get hostId() {
        return this.id;
    }

    @HostBinding('attr.placeholder')
    get hostPlaceholder() {
        return this.placeholder;
    }

    @HostBinding('disabled')
    get hostDisabled() {
        return this.disabled;
    }

    @HostBinding('required')
    get hostRequired() {
        return this.required;
    }

    @HostBinding('attr.readonly')
    get hostReadonly() {
        return this.readonly && !this._isNativeSelect ? '' : null;
    }

    @HostBinding('attr.aria-describedby')
    get hostAriaDescribedBy() {
        return this.getAriaDescribedBy();
    }

    @HostBinding('attr.aria-invalid')
    get hostAriaInvalid() {
        return this.errorState;
    }

    @HostBinding('attr.aria-required')
    get hostAriaRequired() {
        return this.required.toString();
    }

    @HostBinding('attr.aria-disabled')
    get hostAriaDisabled() {
        return this.disabled.toString();
    }

    @HostBinding('attr.aria-label')
    get hostAriaLabel() {
        return this.getAriaLabel();
    }

    @HostListener('blur')
    onBlur() {
        this._focusChanged(false);
    }

    @HostListener('focus')
    onFocus() {
        this._focusChanged(true);
    }

    @HostListener('input')
    _onInput() {
        // This is a noop function and is used to let Angular know whenever the value changes.
        // Angular will run a new change detection each time the `input` event has been dispatched.
        // It's necessary that Angular recognizes the value change, because when floatingLabel
        // is set to false and Angular forms aren't used, the placeholder won't recognize the
        // value changes and will not disappear.
        // Listening to the input event wouldn't be necessary when the input is using the
        // FormsModule or ReactiveFormsModule, because Angular forms also listens to input events.
    }

    /**
     * Implemented as part of RidangoFormFieldControl.
     * @docs-private
     */
    @Input()
    get disabled(): boolean {
        if (this.ngControl && this.ngControl.disabled !== null) {
            return this.ngControl.disabled;
        }
        return this._disabled;
    }
    set disabled(value: boolean) {
        this._disabled = coerceBooleanProperty(value);

        // Browsers may not fire the blur event if the input is disabled too quickly.
        // Reset from here to ensure that the element doesn't become stuck.
        if (this.focused) {
            this.focused = false;
            this.stateChanges.next();
        }
    }

    /**
     * Implemented as part of RidangoFormFieldControl.
     * @docs-private
     */
    @Input()
    get id(): string {
        return this._id;
    }
    set id(value: string) {
        this._id = value || this._uid;
    }

    /**
     * Implemented as part of RidangoFormFieldControl.
     * @docs-private
     */
    @Input()
    get required(): boolean {
        return this._required;
    }
    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
    }

    /** Input type of the element. */
    @Input()
    get type(): string {
        return this._type;
    }
    set type(value: string) {
        this._type = value || 'text';
        this._validateType();

        // When using Angular inputs, developers are no longer able to set the properties on the native
        // input element. To ensure that bindings for `type` work, we need to sync the setter
        // with the native property. Textarea elements don't support the type property or attribute.
        if (!this._isTextarea() && getSupportedInputTypes().has(this._type)) {
            (this._elementRef.nativeElement as HTMLInputElement).type = this._type;
        }
    }

    /**
     * Implemented as part of RidangoFormFieldControl.
     * @docs-private
     */
    @Input()
    get value(): string {
        return this._inputValueAccessor.value;
    }
    set value(value: string) {
        if (value !== this.value) {
            this._inputValueAccessor.value = value;
            this.stateChanges.next();
        }
    }

    /** Whether the element is readonly. */
    @Input()
    get readonly(): boolean {
        return this._readonly;
    }
    set readonly(value: boolean) {
        this._readonly = coerceBooleanProperty(value);
    }

    getAriaLabel(): string {
        const asterisk = this.translate.instant('ASTERISK');
        const element = this._elementRef.nativeElement;
        const label = this.label || element.getAttribute('placeholder') || '';

        const manualLabel =
            element.getAttribute('aria-label') ??
            element.getAttribute('title') ??
            element.getAttribute('formcontrolname') ??
            label;

        if (manualLabel) {
            // both title and aria-label are read by TalkBack, so we remove title to avoid duplication
            element.removeAttribute('title');
        }

        return this.required && manualLabel && !manualLabel.includes(asterisk)
            ? manualLabel + ' ' + asterisk
            : manualLabel;
    }

    getAriaDescribedBy(): string | null {
        if (this._hasCustomDescribedBy) {
            return this._ariaDescribedBy || null;
        }

        return this._elementRef.nativeElement.getAttribute('aria-describedby');
    }

    // eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
    ngOnInit() {
        if (this._platform.isBrowser) {
            this._autofillMonitor.monitor(this._elementRef.nativeElement).subscribe((event) => {
                this.autofilled = event.isAutofilled;
                this.stateChanges.next();
            });
        }
        this.scrollFocusedInputOnKeyboardShow();
    }

    // eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
    ngOnChanges() {
        this.stateChanges.next();
    }

    // eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
    ngOnDestroy() {
        this.stateChanges.complete();

        if (this._platform.isBrowser) {
            this._autofillMonitor.stopMonitoring(this._elementRef.nativeElement);
        }

        this._keyboardListener?.remove();
    }

    // eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
    ngDoCheck() {
        if (this.ngControl) {
            // We need to re-evaluate this on every change detection cycle, because there are some
            // error triggers that we can't subscribe to (e.g. parent form submissions). This means
            // that whatever logic is in here has to be super lean or we risk destroying the performance.
            // TODO:
            this.updateErrorState();
        }

        // We need to dirty-check the native element's value, because there are some cases where
        // we won't be notified when it changes (e.g. the consumer isn't using forms or they're
        // updating the value using `emitEvent: false`).
        this._dirtyCheckNativeValue();
    }

    /** Focuses the input. */
    focus(options?: FocusOptions): void {
        this._elementRef.nativeElement.focus(options);
    }

    /**
     * Implemented as part of RidangoFormFieldControl.
     * @docs-private
     */
    setDescribedByIds(ids: string[]) {
        this._hasCustomDescribedBy = true;
        const existing = this._elementRef.nativeElement.getAttribute('aria-describedby');
        const merged = new Set([...(existing || '').split(' ').filter(Boolean), ...ids]);
        this._ariaDescribedBy = Array.from(merged).join(' ');
    }

    /**
     * Implemented as part of RidangoFormFieldControl.
     * @docs-private
     */
    onContainerClick() {
        // Do not re-focus the input element if the element is already focused. Otherwise it can happen
        // that someone clicks on a time input and the cursor resets to the "hours" field while the
        // "minutes" field was actually clicked. See: https://github.com/angular/components/issues/12849
        if (!this.focused) {
            this.focus();
        }
    }

    /** Callback for the cases where the focused state of the input changes. */
    _focusChanged(isFocused: boolean) {
        if (isFocused !== this.focused && (!this.readonly || !isFocused)) {
            this.focused = isFocused;
            this.stateChanges.next();
        }
    }

    /** Does some manual dirty checking on the native input `value` property. */
    protected _dirtyCheckNativeValue() {
        const newValue = this._elementRef.nativeElement.value;

        if (this._previousNativeValue !== newValue) {
            this._previousNativeValue = newValue;
            this.stateChanges.next();
        }
    }

    /** Make sure the input is a supported type. */
    protected _validateType() {
        // TODO:
        // if ( MAT_INPUT_INVALID_TYPES.indexOf( this._type ) > -1 ) {
        //     throw getMatInputUnsupportedTypeError( this._type );
        // }
    }

    /** Checks whether the input type is one of the types that are never empty. */
    protected _isNeverEmpty() {
        return this._neverEmptyInputTypes.indexOf(this._type) > -1;
    }

    /** Checks whether the input is invalid based on the native validation. */
    protected _isBadInput() {
        // The `validity` property won't be present on platform-server.
        const validity = (this._elementRef.nativeElement as HTMLInputElement).validity;
        return validity && validity.badInput;
    }

    /** Determines if the component host is a textarea. */
    protected _isTextarea() {
        return this._elementRef.nativeElement.nodeName.toLowerCase() === 'textarea';
    }

    /**
     * Implemented as part of RidangoFormFieldControl.
     * @docs-private
     */
    get empty(): boolean {
        return (
            !this._isNeverEmpty() && !this._elementRef.nativeElement.value && !this._isBadInput() && !this.autofilled
        );
    }

    /**
     * Implemented as part of RidangoFormFieldControl.
     * @docs-private
     */
    get shouldLabelFloat(): boolean {
        if (this._isNativeSelect) {
            // For a single-selection `<select>`, the label should float when the selected option has
            // a non-empty display value. For a `<select multiple>`, the label *always* floats to avoid
            // overlapping the label with the options.
            const selectElement = this._elementRef.nativeElement as HTMLSelectElement;
            const firstOption: HTMLOptionElement | undefined = selectElement.options[0];

            // On most browsers the `selectedIndex` will always be 0, however on IE and Edge it'll be
            // -1 if the `value` is set to something, that isn't in the list of options, at a later point.
            return (
                this.focused ||
                selectElement.multiple ||
                !this.empty ||
                !!(selectElement.selectedIndex > -1 && firstOption && firstOption.label)
            );
        } else {
            return this.focused || !this.empty;
        }
    }

    private async scrollFocusedInputOnKeyboardShow(): Promise<void> {
        if (Capacitor.isNativePlatform()) {
            this._keyboardListener = await Keyboard.addListener('keyboardDidShow', () => {
                const el = this._elementRef.nativeElement;

                // Only scroll the currently focused input
                if (document.activeElement === el) {
                    el.scrollIntoView({
                        block: 'center'
                    });
                }
            });
        }
    }
}
